// =====================================================================================
// INVRT Radar — web console — account — delivery — delivery targets
//
// docs/API-CONTRACTS-C1.md §6 (api/routes/delivery.ts): the list, "Add target"
// (TargetForm), test, pause or resume, edit label and events, delete, and the attempt
// history drawer.
//
// WHAT A ROW SHOWS. The server's own Target: kind (with its friendly name), label, site
// scope (resolved to the site's domain through GET /sites, or "All sites"), events,
// status, last delivery, last error. Its `config` is the MASKED read shape: an email
// target's recipients, and for every other kind the server's `token_hint` (the last four
// characters) beside the non-secret field. No credential is ever on this page; the only
// way to change one is to delete the target and add it again with the new token.
//
// TEST SENDS are synchronous: the server delivers ONE clearly marked test contact
// ("Radar Test Contact", `test: true`) and reports what the destination did. A refusal is
// a RESULT shown inline under the label, not an error state.
//
// STATUS. `active` and `paused` are the tenant's to flip (one PATCH). `disabled` is set
// by an operator and shown as such; the tenant can still resume it, which re-queues every
// attempt that waited.
// =====================================================================================

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useApiClient, useSites } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import type {
  DeleteDeliveryTargetResponse,
  DeliveryTarget,
  UpdateDeliveryTargetRequest,
  WebhookEventKind,
} from "@/lib/types.c1";
import { AttemptsDrawer } from "./AttemptsDrawer";
import { EventKindBadges, TargetStatusBadge } from "./badges";
import { EventKindCheckboxes } from "./EventKindCheckboxes";
import {
  TARGET_KIND_LABELS,
  targetsKey,
  useDeliveryTargets,
  type TargetTestOutcome,
} from "./hooks";
import { TargetForm } from "./TargetForm";

// ---------------------------------------------------------------------------------------
// Edit / delete
// ---------------------------------------------------------------------------------------

function EditTargetDialog({ target, onClose }: { target: DeliveryTarget; onClose: () => void }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId } = useSession();
  const { push } = useToast();
  const [label, setLabel] = useState(target.label);
  const [events, setEvents] = useState<WebhookEventKind[]>(target.events);

  const update = useMutation({
    mutationFn: (body: UpdateDeliveryTargetRequest) =>
      api.patch<DeliveryTarget>(`/delivery/targets/${target.id}`, body),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: targetsKey(orgId) });
      push({ tone: "success", title: "Target updated", body: updated.label });
      onClose();
    },
  });

  const valid = label.trim() !== "" && events.length > 0;

  return (
    <Dialog
      open
      onClose={onClose}
      title="Edit this target"
      description={`${target.label} (${TARGET_KIND_LABELS[target.kind]})`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => update.mutate({ label: label.trim(), events })}
            loading={update.isPending}
            disabled={!valid}
          >
            Save changes
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="Label"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          maxLength={80}
        />
        <EventKindCheckboxes
          legend="Events to deliver"
          hint="Tick at least one."
          value={events}
          onChange={setEvents}
        />
        <p className="text-xs text-ink-muted">
          To change the destination or its credentials, delete this target and add it
          again. Radar never shows a stored credential back.
        </p>
        {update.isError ? <ErrorState error={update.error} /> : null}
      </div>
    </Dialog>
  );
}

function DeleteTargetDialog({ target, onClose }: { target: DeliveryTarget; onClose: () => void }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId } = useSession();
  const { push } = useToast();

  const remove = useMutation({
    mutationFn: () => api.delete<DeleteDeliveryTargetResponse>(`/delivery/targets/${target.id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: targetsKey(orgId) });
      push({ tone: "success", title: "Target deleted", body: target.label });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Delete this target?"
      description={`${target.label} (${TARGET_KIND_LABELS[target.kind]})`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={remove.isPending}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => remove.mutate()} loading={remove.isPending}>
            Delete target
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-ink-muted">
        <p>
          Radar stops sending contacts there at once, and any stored credential is deleted
          from its vault. Contacts already delivered are untouched. If you only want a
          break, pause it instead.
        </p>
        {remove.isError ? <ErrorState error={remove.error} /> : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------
// One row
// ---------------------------------------------------------------------------------------

/** The non-secret half of a target's masked config, for the destination cell. */
function describeDestination(target: DeliveryTarget): string {
  switch (target.kind) {
    case "email":
      return target.config.recipients.join(", ");
    case "hubspot":
      return target.config.lifecycle_stage
        ? `Token ${target.config.token_hint}, lifecycle stage ${target.config.lifecycle_stage}`
        : `Token ${target.config.token_hint}`;
    case "pipedrive":
      return `${target.config.company_domain}.pipedrive.com, token ${target.config.token_hint}`;
    case "invrt_crm":
    case "monti":
      return `${target.config.endpoint_url}, key ${target.config.token_hint}`;
    default:
      return "";
  }
}

function describeOutcome(outcome: TargetTestOutcome): string {
  const status = typeof outcome.status_code === "number" ? `HTTP ${outcome.status_code}` : null;
  if (outcome.ok) return status ? `Delivered: ${status}` : "Delivered";
  const reason = outcome.error ?? status ?? "No answer from the destination.";
  return `Failed: ${reason}`;
}

function TargetRow({
  target,
  siteLabel,
  isAdmin,
  outcome,
  testing,
  onTest,
  toggling,
  onToggle,
  onEdit,
  onDelete,
  onAttempts,
}: {
  target: DeliveryTarget;
  siteLabel: string;
  isAdmin: boolean;
  outcome: TargetTestOutcome | null;
  testing: boolean;
  onTest: () => void;
  toggling: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onAttempts: () => void;
}) {
  const active = target.status === "active";
  return (
    <TableRow>
      <TableCell className="max-w-xs">
        <div className="flex flex-col gap-1">
          <span>
            <Badge tone="neutral">{TARGET_KIND_LABELS[target.kind]}</Badge>
          </span>
          <p className="break-words text-xs text-ink-muted">{describeDestination(target)}</p>
        </div>
      </TableCell>
      <TableCell className="font-medium">
        {target.label}
        {outcome ? (
          <p
            className={cn("mt-1 text-xs font-normal", outcome.ok ? "text-accent-700" : "text-danger")}
            role="status"
          >
            {describeOutcome(outcome)}
          </p>
        ) : null}
      </TableCell>
      <TableCell className="text-ink-muted">{siteLabel}</TableCell>
      <TableCell>
        <EventKindBadges events={target.events} />
      </TableCell>
      <TableCell>
        <TargetStatusBadge status={target.status} />
      </TableCell>
      <TableCell className="whitespace-nowrap text-ink-muted">
        {target.last_delivery_at ? formatDateTime(target.last_delivery_at) : "Never"}
      </TableCell>
      <TableCell className="max-w-xs text-xs text-ink-muted">{target.last_error ?? "—"}</TableCell>
      <TableCell className="text-right">
        <span className="flex flex-wrap justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            aria-label={`Attempts for ${target.label}`}
            onClick={onAttempts}
          >
            Attempts
          </Button>
          {isAdmin ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                aria-label={`Test ${target.label}`}
                onClick={onTest}
                loading={testing}
              >
                Test
              </Button>
              <Button
                variant="secondary"
                size="sm"
                aria-label={`${active ? "Pause" : "Resume"} ${target.label}`}
                onClick={onToggle}
                loading={toggling}
              >
                {active ? "Pause" : "Resume"}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                aria-label={`Edit ${target.label}`}
                onClick={onEdit}
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-danger hover:text-danger"
                aria-label={`Delete ${target.label}`}
                onClick={onDelete}
              >
                Delete
              </Button>
            </>
          ) : null}
        </span>
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------------------
// The section
// ---------------------------------------------------------------------------------------

export function TargetsSection({ isAdmin }: { isAdmin: boolean }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId } = useSession();
  const targets = useDeliveryTargets();
  const sites = useSites();

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<DeliveryTarget | null>(null);
  const [deleting, setDeleting] = useState<DeliveryTarget | null>(null);
  const [viewing, setViewing] = useState<DeliveryTarget | null>(null);
  const [outcomes, setOutcomes] = useState<Record<string, TargetTestOutcome>>({});

  const test = useMutation({
    mutationFn: (target: DeliveryTarget) =>
      api.post<TargetTestOutcome>(`/delivery/targets/${target.id}/test`),
    onSuccess: (outcome, target) => {
      setOutcomes((current) => ({ ...current, [target.id]: outcome }));
    },
  });

  const toggle = useMutation({
    mutationFn: (target: DeliveryTarget) => {
      const body: UpdateDeliveryTargetRequest = {
        status: target.status === "active" ? "paused" : "active",
      };
      return api.patch<DeliveryTarget>(`/delivery/targets/${target.id}`, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: targetsKey(orgId) });
    },
  });

  function siteLabel(siteId: string | null): string {
    if (siteId === null) return "All sites";
    const site = sites.data?.sites.find((candidate) => candidate.id === siteId);
    return site ? site.domain : `Site ${siteId.slice(0, 8)}`;
  }

  return (
    <section aria-label="Delivery targets" className="flex flex-col gap-4">
      <Card>
        <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Delivery targets</CardTitle>
            <CardDescription className="mt-1">
              Where each new contact goes the moment Radar has it: an inbox, HubSpot,
              Pipedrive, INVRT CRM or MONTI. Credentials are checked with the destination
              before they are stored, and are never shown back.
            </CardDescription>
          </div>
          {isAdmin ? (
            <Button onClick={() => setAdding(true)}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add target
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {test.isError ? <ErrorState error={test.error} /> : null}
          {toggle.isError ? <ErrorState error={toggle.error} /> : null}

          {targets.isLoading ? <LoadingState label="Loading your targets…" /> : null}
          {targets.isError ? (
            <ErrorState error={targets.error} onRetry={() => void targets.refetch()} />
          ) : null}

          {targets.isSuccess ? (
            targets.data.targets.length === 0 ? (
              <EmptyState
                title="No delivery targets yet"
                body={
                  isAdmin
                    ? "Add a target and every identified visitor or revealed contact is pushed to it as it arrives."
                    : "An administrator on your team can add one. Targets your organisation has set up appear here."
                }
                {...(isAdmin
                  ? { action: <Button onClick={() => setAdding(true)}>Add target</Button> }
                  : {})}
              />
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Destination</TableHeaderCell>
                    <TableHeaderCell>Label</TableHeaderCell>
                    <TableHeaderCell>Site scope</TableHeaderCell>
                    <TableHeaderCell>Events</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Last delivery</TableHeaderCell>
                    <TableHeaderCell>Last error</TableHeaderCell>
                    <TableHeaderCell className="text-right">Actions</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {targets.data.targets.map((target) => (
                    <TargetRow
                      key={target.id}
                      target={target}
                      siteLabel={siteLabel(target.site_id)}
                      isAdmin={isAdmin}
                      outcome={outcomes[target.id] ?? null}
                      testing={test.isPending && test.variables?.id === target.id}
                      onTest={() => test.mutate(target)}
                      toggling={toggle.isPending && toggle.variables?.id === target.id}
                      onToggle={() => toggle.mutate(target)}
                      onEdit={() => setEditing(target)}
                      onDelete={() => setDeleting(target)}
                      onAttempts={() => setViewing(target)}
                    />
                  ))}
                </TableBody>
              </Table>
            )
          ) : null}
        </CardContent>
      </Card>

      {adding ? <TargetForm onClose={() => setAdding(false)} /> : null}
      {editing ? <EditTargetDialog target={editing} onClose={() => setEditing(null)} /> : null}
      {deleting ? <DeleteTargetDialog target={deleting} onClose={() => setDeleting(null)} /> : null}
      {viewing ? (
        <AttemptsDrawer target={viewing} isAdmin={isAdmin} onClose={() => setViewing(null)} />
      ) : null}
    </section>
  );
}
